import { userService } from '@/domain/services/user/userService';
import { OnboardInfoRemapper } from '@/domain/remappers/user/onboardInfoRemapper';
import { UserUtils } from '@/domain/utils/userUtils';
import { appLogger } from '@/domain/services/analytics/appLogger';
import type { OnboardInfoInput } from '@/domain/input/user/onboardInfoInput';
import type { EditUserProfileResponse } from '@/domain/apiResponses/user/editUserProfileResponse';

/**
 * Saves the current user's profile, resolving the profile image first.
 *
 * Step-for-step port of Flutter's `UsersRepository.saveUserDetails`
 * (lib/data/repository/users_repository.dart:54-93). Side-effect ordering is a
 * parity rule — keep the sequence: resolve image → compute the re-upload
 * predicate → remap → call the callable.
 *
 * ONE deliberate divergence: a *changed* image whose upload fails aborts the
 * save instead of persisting the resulting `null` (which would wipe the stored
 * avatar). See the guard below. Bug found on Flutter, web implemented
 * correctly; follow-up recommended to fix Flutter bug
 * (`users_repository.dart:68-71`, `sync_repository.dart:601-604`).
 */
export class EditUserProfileUseCase {
  async execute(input: OnboardInfoInput): Promise<EditUserProfileResponse> {
    // One try/catch wraps the whole body, collapsing any throw to
    // `success = false` — `users_repository.dart:64` + `:88-90`.
    try {
      // `:65-67` — a non-null image that is NOT already in the profiles bucket
      // means the user picked a new one.
      const changedProfileImage =
        input.profileImage !== null &&
        !UserUtils.isProfileImageInProfilesBucket(input.profileImage);

      // `:68-71` — upload only when changed; otherwise pass the input through
      // unchanged. Unchanged → passthrough is deliberate, do NOT "fix" it: an
      // untouched avatar re-persists the FULL read URL rather than the short
      // form. Both satisfy Flutter's `User.profileImageUploaded` `contains` test
      // (user.dart:108-110), so both clients keep rendering. Normalising here
      // would be a silent cross-client divergence.
      const uploadedProfileImage = !changedProfileImage
        ? input.profileImage
        : await userService.uploadUserProfileImage(input.profileImage!);

      // DELIBERATE, DOCUMENTED DIVERGENCE from users_repository.dart:68-71.
      // Flutter persists a failed upload's `null`, which the `onboardUser`
      // callable writes through a merge set (onboard_user.ts:34,47) — wiping
      // `profile_image` with no error shown, because the callable itself
      // succeeded. That is tolerable on Flutter only because its failure modes
      // are native-client rare: its download arm (app_storage_service.dart:338)
      // and its `putFile` are not subject to CORS. On web both are browser calls
      // behind a preflight, so the destructive path is ordinary rather than
      // exceptional. We therefore fail the save loudly and persist NOTHING; the
      // page shows `editUserProfile.saveError*` and the user retries with their
      // avatar intact.
      //
      // This matches the web's own precedent for this same primitive:
      // syncSecureStorageUseCase.syncUploadProfileImage already returns early
      // rather than persisting a null (Flutter's sync does persist it —
      // sync_repository.dart:601-604).
      //
      // Guard on `changedProfileImage &&` — NOT a bare `!uploadedProfileImage` —
      // so the legitimate no-avatar case (`profileImage: null`, untouched) still
      // persists its null.
      if (changedProfileImage && !uploadedProfileImage) {
        appLogger.error(
          'Profile image upload failed; aborting save rather than persisting a null that would wipe the avatar',
          'EditUserProfileUseCase.execute'
        );
        return { success: false, profileImageReuploaded: false };
      }

      const profileImage = uploadedProfileImage;

      // `:73` — the predicate Flutter gates its cache eviction on. Evaluated
      // BEFORE and INDEPENDENTLY of the callable result (`:87`): the eviction
      // happens even if the save then fails, so this is never gated on `success`.
      // `!!profileImage` ports Dart's `!profileImage.isNullOrEmpty()` — null AND
      // '' both resolve false. Kept verbatim as the parity record, though the
      // abort guard above means a *changed* image is always truthy by here, so
      // the `!!` is now defensive rather than load-bearing.
      const profileImageReuploaded = changedProfileImage && !!profileImage;

      // `:77-85` — the DTO is assembled from the RESOLVED image, not the input
      // one. `OnboardInfoRemapper.toDto` owns the `generateUserNameInfo` mapping
      // Flutter does at `:77`; never hand-build the DTO here.
      const dto = OnboardInfoRemapper.toDto({ ...input, profileImage });

      // `:87`
      const success = await userService.editUserProfile(dto);

      return { success, profileImageReuploaded };
    } catch (error) {
      // `:88-90` — Flutter's catch is a bare swallow to `success = false`; the
      // log is additive observability, not a behaviour change.
      appLogger.error('Error saving user profile', 'EditUserProfileUseCase.execute', {
        error: error as Error,
      });
      return { success: false, profileImageReuploaded: false };
    }
  }
}

export const editUserProfileUseCase = new EditUserProfileUseCase();
